import React, { useState, useEffect, useContext, Suspense, useCallback, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import useResponsive from "../hooks/useResponsive";
import { CartContext } from "../Contexts/CartContext";
import useProductData from "../hooks/useProductData";
import { motion, AnimatePresence } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "./Home_Mobile.css";
import cartIcon from "../resources/icons/cart.png";
import useEmblaCarousel from "embla-carousel-react";
import { DotButton, useDotButton } from "../components/mobile/EmblaCarouselDotButton";
import { PrevButton, NextButton, usePrevNextButtons } from "../components/mobile/EmblaCarouselArrowButtons";
import "../components/mobile/embla.css";
import { Scrollbar } from "swiper/modules";
import "swiper/css/scrollbar";
import simbol1 from "../resources/images/symbol4.png";
import Footer from "../components/shared/Footer";
import symbol3 from "../resources/images/symbol3.png";
import About_Mobile from "./About_Mobile";
import MobileTopBar from "../components/mobile/MobileTopBar";
import { useNavigate, useParams } from "react-router-dom";
import icondownload from "../resources/icons/download.png";
import { various, navigation } from "../Data/siteContent";

const ReactPlayer = React.lazy(() => import("react-player"));

const VideoEmblaCarousel = React.memo(({ slides }) => {
  const options = { loop: false };
  const [emblaRef, emblaApi] = useEmblaCarousel(options);

  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(emblaApi);
  const { prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick } = usePrevNextButtons(emblaApi);

  const [loading, setLoading] = useState(true);

  return (
    <div className="embla">
      <div className="embla__viewport" ref={emblaRef}>
        <div className="embla__container">
          {slides.map((slide, index) => (
            <div className="embla__slide" key={index}>
              {slide.isVideo ? (
                slide.isYouTubeVideo ? (
                  <div className="video-wrapper">
                    {loading && <div className="loader">{various.loading}</div>}{" "}
                    <Suspense fallback={<div>{various.loadingVideo}</div>}>
                      <ReactPlayer
                        url={slide.videoUrl}
                        controls
                        width="100%"
                        height="100%"
                        className="react-player-absolute"
                        onReady={() => setLoading(false)}
                        onStart={() => setLoading(false)}
                      />
                    </Suspense>
                  </div>
                ) : (
                  <video controls className="mobile-video-native">
                    <source src={slide.videoUrl} type="video/mp4" />
                    {various.unsupportedBrowser}
                  </video>
                )
              ) : (
                <img src={slide.image} alt={slide.title} loading="lazy" />
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="embla__controls">
        <div className="embla__buttons">
          <PrevButton onClick={onPrevButtonClick} disabled={prevBtnDisabled} />
          <NextButton onClick={onNextButtonClick} disabled={nextBtnDisabled} />
        </div>
        <div className="embla__dots">
          {scrollSnaps.map((_, index) => (
            <DotButton
              key={index}
              onClick={() => onDotButtonClick(index)}
              className={"embla__dot".concat(index === selectedIndex ? " embla__dot--selected" : "")}
            />
          ))}
        </div>
      </div>
    </div>
  );
});

VideoEmblaCarousel.displayName = "VideoEmblaCarousel";

const Home_Mobile = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [selectedSection, setSelectedSection] = useState("Products");

  const [selectedTab, setSelectedTab] = useState("Overview");
  const { cartItems, addToCart, removeFromCart, updateQuantity, totalItemCount } = useContext(CartContext);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImageSrc, setModalImageSrc] = useState(null);
  const isMobile = useResponsive();
  const [showDuplicatedTabs, setShowDuplicatedTabs] = useState(false);
  const productTabsRef = useRef(null);
  const [showEmptyCartPopup, setShowEmptyCartPopup] = useState(false);
  const footerRef = useRef(null);
  const navigate = useNavigate();

  const { productName } = useParams();

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [tappedComingSoon, setTappedComingSoon] = useState(null);
  const { products, availability, availableQty, apiPrice } = useProductData(selectedProduct, isMobile);

  useEffect(() => {
    const handleScroll = () => {
      if (!productTabsRef.current || !footerRef.current) return;

      const scrollY = window.scrollY;
      const productTabsTop = productTabsRef.current.offsetTop;
      const productTabsHeight = productTabsRef.current.offsetHeight;

      let shouldShowDuplicatedTabs = false;
      if (selectedTab === "Overview" && scrollY > productTabsTop + productTabsHeight) {
        shouldShowDuplicatedTabs = true;
      }

      const footerRect = footerRef.current.getBoundingClientRect();
      const footerIsVisible = footerRect.top < window.innerHeight && footerRect.bottom >= 0;

      if (footerIsVisible) {
        shouldShowDuplicatedTabs = false;
      }

      setShowDuplicatedTabs(shouldShowDuplicatedTabs);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [selectedTab]);

  const [selectedIndex] = useState(0);

  const selectedProductData = products[selectedProduct];

  const initialSlidePositions = Object.keys(products).reduce((acc, key) => {
    acc[key] = { Overview: 0, Photos: 0, Videos: 0, Details: 0 };
    return acc;
  }, {});

  const [slidePositions, setSlidePositions] = useState(initialSlidePositions);

  const getSlidesData = useCallback(
    (productKey, tab) => {
      const product = products[productKey];
      if (!product || !product.boxContents) return [];

      switch (tab) {
        case "Overview":
          return (
            product.boxContents?.[0]?.map(content => ({
              title: product.name,
              secondTitle: product.secondName,
              description: content.details,
              image: content.image,
              isVideo: false,
            })) || []
          );
        case "Photos": {
          const photosContent = product.boxContents?.[1]?.[0];
          return (
            photosContent?.images?.map((image, idx) => ({
              title: `Photo ${idx + 1}`,
              description: "",
              image: image,
              isVideo: false,
            })) || []
          );
        }
        case "Videos":
          return (
            product.boxContents?.[3]?.[0]?.videos?.map(video => ({
              title: "Video",
              description: "",
              videoUrl: video.videoUrl,
              isVideo: true,
              isYouTubeVideo: video.isYouTubeVideo,
            })) || []
          );
        case "Details":
          return (
            product.boxContents?.[4]?.map(section => ({
              title: section.title,
              description: section.details,
              features: section.sections?.features,
              specifications: section.sections?.specifications,
            })) || []
          );
        default:
          return [];
      }
    },
    [products]
  );

  const slides = useMemo(
    () => getSlidesData(selectedProduct, selectedTab),
    [selectedProduct, selectedTab, getSlidesData]
  );

  const renderVideoSlides = useCallback(() => {
    if (!slides || slides.length === 0) return <div>{various.noVideosAvailable}</div>;

    return <VideoEmblaCarousel slides={slides} />;
  }, [slides]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleCart = () => {
    setIsCartOpen(!isCartOpen);
  };

  const handleSectionChange = section => {
    setSelectedSection(section);
    setIsMenuOpen(false);

    if (section === "About") {
      navigate("/about");
    }
    if (section === "Products") {
      navigate("/products");
    }
  };

  useEffect(() => {
    if (productName && products[productName]) {
      setSelectedProduct(productName);
    } else if (!productName) {
      setSelectedProduct(null);
    }
  }, [productName, products]);

  const handleProductClick = product => {
    setSelectedProduct(product);
    setSelectedTab("Overview");
    if (product) {
      navigate(`/products/${product}`);
    } else {
      navigate("/products");
    }
  };

  const handleSlideChange = index => {
    setSlidePositions(prev => ({
      ...prev,
      [selectedProduct]: {
        ...prev[selectedProduct],
        [selectedTab]: index,
      },
    }));
  };

  const renderOverviewContent = () => {
    const slides = getSlidesData(selectedProduct, "Overview");
    if (!slides) return null;
    return (
      <div className="overview-content-mobile">
        {slides.map((slide, index) => (
          <div key={index} className="overview-slide-mobile">
            <img src={slide.image} alt={slide.title} loading="lazy" />
            <div className="overview-text-mobile">
              <div dangerouslySetInnerHTML={{ __html: slide.description }}></div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderSlides = () => {
    const slides = getSlidesData(selectedProduct, selectedTab);
    if (!slides) return null;

    return slides.map((slide, index) => (
      <SwiperSlide key={index}>
        <div className="slide-content">
          {selectedTab === "Photos" && (
            <div
              className="zoom-button"
              onClickCapture={e => {
                e.preventDefault();
                e.stopPropagation();
                setModalImageSrc(slide.image);
                setIsModalOpen(true);
              }}
              onTouchStart={e => {
                e.stopPropagation();
              }}
            ></div>
          )}
          <div className="container-slider-img-text">
            <img src={slide.image} alt={slide.title} loading="lazy" />
          </div>
        </div>
      </SwiperSlide>
    ));
  };

  const renderDetails = () => {
    if (!selectedProductData || !selectedProductData.boxContents || !selectedProductData.boxContents[4]) {
      return <div>{various.noDetailsAvailable}</div>;
    }

    const detailsSection = selectedProductData.boxContents[4][0];
    if (!detailsSection || !detailsSection.sections) {
      return <div>{various.noDetailsShort}</div>;
    }

    const { features, specifications } = detailsSection.sections;
    const extraFeatures = selectedProductData.extraFeatures || [];
    const firmwareFeature = extraFeatures.find(f => f.type === "firmware-updates");
    const midiFeature = extraFeatures.find(f => f.type === "settings-editor");

    return (
      <div className="container-details-section-mobile">
        <div className="details-section">
          <div className="manual-pdf-box">
            <a href={selectedProductData.manualFile} target="_blank" rel="noopener noreferrer">
              <img src={icondownload} alt="pdf-icon" />
              {various.pdfManual}
            </a>
          </div>
          {features && (
            <div className="details-item">
              <div className="details-header">
                <span>{various.features}</span>
              </div>
              <div className="details-content" dangerouslySetInnerHTML={{ __html: features }} />
            </div>
          )}

          {specifications && (
            <div className="details-item">
              <div className="details-header">
                <span>{various.specifications}</span>
              </div>
              <div className="details-content" dangerouslySetInnerHTML={{ __html: specifications }} />
            </div>
          )}

          {firmwareFeature && (
            <div className="details-item">
              <div className="details-header">
                <span>{various.firmwareUpdates}</span>
              </div>
              <div className="details-content details-firmware">
                <p>
                  <strong>Current firmware version:</strong> {firmwareFeature.firmwareVersion}
                  <br />
                  <strong>Release date:</strong> {firmwareFeature.firmwareDate}
                </p>
                <Link
                  className="details-firmware-link"
                  to={`/products/${selectedProductData.id}/${firmwareFeature.routeSlug}`}
                >
                  Download &amp; update instructions →
                </Link>
              </div>
            </div>
          )}

          {midiFeature && (
            <div className="details-item">
              <div className="details-header">
                <span>{various.settingsEditor}</span>
              </div>
              <div className="details-content">
                <p>{various.settingsEditorMobileNote}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const handleAddToCart = () => {
    if (apiPrice === null) return;
    const productDetails = products[selectedProduct];
    const productName = productDetails?.name || selectedProduct;
    const currentInCart = cartItems.find(item => item.name === productName);
    if (availableQty !== null && (currentInCart?.quantity || 0) >= availableQty) return;

    const productToAdd = {
      name: productName,
      price: `${apiPrice}€`,
      secondTitle: productDetails?.boxContents[0]?.[0]?.secondTitle || "",
      image: productDetails?.boxContents[0]?.[0]?.image || "",
    };
    addToCart(productToAdd);
  };

  const handleOutOfStockClick = () => {
    navigate("/out-of-stock", { state: { productName: selectedProductData?.name } });
  };

  const getCartItemAvailableQty = item => {
    const productByName = Object.values(products).find(product => product.name === item.name);
    if (!productByName) return null;
    const itemProductInfo = availability[productByName.id];
    return itemProductInfo ? itemProductInfo.available : null;
  };

  useEffect(() => {
    const path = window.location.pathname;

    if (path === "/") {
      setSelectedSection("Products");
      navigate("/products");
    }

    if (path === "/about") {
      setSelectedSection("About");
    }

    const productMatch = path.match(/^\/products\/(.+)$/);
    if (productMatch) {
      const productNameFromURL = productMatch[1];
      if (products[productNameFromURL]) {
        setSelectedSection("Products");
        setSelectedProduct(productNameFromURL);
      }
    }
  }, [navigate, products]);

  const handleGoClick = e => {
    e.preventDefault();
    navigate("/checkout");
  };

  const topLineVariants = {
    closed: { rotate: 0, x: "-50%", y: "-300%" },
    open: { rotate: 45, x: "-50%", y: "-50%" },
  };

  const middleLineVariants = {
    closed: { opacity: 1, x: "-50%", y: "-50%" },
    open: { opacity: 0 },
  };

  const bottomLineVariants = {
    closed: { rotate: 0, x: "-50%", y: "200%" },
    open: { rotate: -45, x: "-50%", y: "-50%" },
  };

  return (
    <div className="mobile-version">
      <MobileTopBar />

      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsModalOpen(false)}
          >
            <img src={modalImageSrc} alt="Detailed view" />
          </motion.div>
        )}
      </AnimatePresence>

      <div className="menu-bar">
        <button className="burger-icon" onClick={toggleMenu}>
          <motion.div
            className="burger-line"
            variants={topLineVariants}
            animate={isMenuOpen ? "open" : "closed"}
            transition={{ duration: 0.3 }}
          />
          <motion.div
            className="burger-line"
            variants={middleLineVariants}
            animate={isMenuOpen ? "open" : "closed"}
            transition={{ duration: 0 }}
          />
          <motion.div
            className="burger-line"
            variants={bottomLineVariants}
            animate={isMenuOpen ? "open" : "closed"}
            transition={{ duration: 0.3 }}
          />
        </button>

        <span className="selected-section">{selectedSection}</span>

        <div className="cart-icon-wrapper" onClick={toggleCart}>
          <img src={cartIcon} className="cart-icon-mobile" alt="Carrello" loading="lazy" />
          {totalItemCount > 0 && <div className="cart-count">{totalItemCount}</div>}
        </div>
      </div>

      <AnimatePresence>
        {isCartOpen && (
          <motion.div
            className="cart-popup-mobile"
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            transition={{ duration: 0.5 }}
          >
            <div className="cart-header">
              <h2>Shopping Cart</h2>
              <button className="close-button" onClick={toggleCart}>
                ✕
              </button>
            </div>
            {cartItems.length > 0 ? (
              <ul>
                {cartItems.map((item, index) => {
                  const itemAvailableQty = getCartItemAvailableQty(item);
                  return (
                    <li key={index} className="li-shopping-cart">
                      <img src={item.image} alt={item.name} />
                      <span>{item.name}</span>
                      <div className="quantity-controls">
                        <button
                          className="quantity-btn"
                          onClick={() => updateQuantity(item.name, (item.quantity || 1) - 1)}
                        >
                          −
                        </button>
                        <span className="quantity-value">{item.quantity || 1}</span>
                        <button
                          className="quantity-btn"
                          disabled={itemAvailableQty !== null && (item.quantity || 1) >= itemAvailableQty}
                          onClick={() => updateQuantity(item.name, (item.quantity || 1) + 1)}
                        >
                          +
                        </button>
                      </div>
                      <button className="remove-button" onClick={() => removeFromCart(item.name)}>
                        ✕
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p>{various.noItems}</p>
            )}
            {showEmptyCartPopup && (
              <div className="popup">
                <p>{various.noItems}</p>
                <button onClick={() => setShowEmptyCartPopup(false)}>{various.close}</button>
              </div>
            )}
            <button
              className={`go-button go-button-mobile ${cartItems.length === 0 ? "go-button--disabled" : ""}`}
              disabled={cartItems.length === 0}
              onClick={handleGoClick}
            >
              {various.checkout}
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            className="dropdown-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.1 }}
          >
            <ul className="menu-list">
              <motion.li
                onClick={() => handleSectionChange("About")}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0 }}
              >
                {navigation.about}
              </motion.li>
              <motion.li
                onClick={() => handleSectionChange("Products")}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0 }}
              >
                {navigation.products}
              </motion.li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="content">
        {selectedSection === "Products" ? (
          <>
            <div className="product-selection">
              {Object.keys(products).map(product => {
                const isActive = availability[products[product].id]?.active;
                return (
                  <div
                    key={product}
                    className={`product ${selectedProduct === product ? "selected" : ""} ${!isActive ? "coming-soon" : ""}`}
                    onClick={() => {
                      if (isActive) {
                        handleProductClick(product);
                      } else {
                        setTappedComingSoon(product);
                        setTimeout(() => setTappedComingSoon(null), 1500);
                      }
                    }}
                    style={!isActive ? { cursor: "default" } : undefined}
                  >
                    <div className="container-product-info">
                      <span>{products[product].name}</span>
                      <div>
                        <img
                          src={
                            products[product].boxContents
                              ? products[product].boxContents[0][0].image
                              : products[product].image
                          }
                          alt={products[product].name}
                          loading="lazy"
                        />
                      </div>
                    </div>
                    {!isActive && (
                      <div className={`coming-soon-label ${tappedComingSoon === product ? "visible" : ""}`}>
                        COMING SOON
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {selectedProduct ? (
              <>
                <div className="product-header">
                  <div className="product-header-mobile">
                    <img src={symbol3} alt="" />
                    <div className="product-title-second-title-mobile-container">
                      <div className="product-title-mobile">{selectedProductData.name}</div>
                      <div className="product-second-title-mobile">{selectedProductData.secondName}</div>
                    </div>
                    <img src={symbol3} alt="" />
                  </div>
                  <div className="container-price-button-buy">
                    <div className="div-container-price-symbol-mobile">
                      <div>
                        {apiPrice !== null && (
                          <>
                            Price: <span className="product-price">{apiPrice}€</span>
                          </>
                        )}
                      </div>
                      <img className="symbol-price-bar-mobile" src={simbol1} alt={"-"} loading="lazy" />
                    </div>
                    <AnimatePresence initial={false}>
                      {availableQty !== null &&
                        (availableQty > 0 ? (
                          <motion.button
                            key={selectedProduct}
                            className="buy-button-mobile"
                            onClick={handleAddToCart}
                            initial={{ x: "100%", opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            exit={{ x: "100%", opacity: 0 }}
                            transition={{ duration: 0.5 }}
                          >
                            {various.buyIt}
                          </motion.button>
                        ) : (
                          <motion.button
                            key={selectedProduct}
                            className="buy-button-mobile buy-button-mobile-out-of-stock"
                            onClick={handleOutOfStockClick}
                            initial={{ x: "100%", opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            exit={{ x: "100%", opacity: 0 }}
                            transition={{ duration: 0.5 }}
                          >
                            {various.outOfStockButton}
                          </motion.button>
                        ))}
                    </AnimatePresence>
                  </div>
                </div>

                <div>
                  <div
                    className={`product-tabs duplicated-tabs duplicated-tabs-fixed ${showDuplicatedTabs ? "show" : "hide"}`}
                  >
                    {various.productTabs.map(tab => (
                      <div
                        key={tab}
                        className={`product-tab ${selectedTab === tab ? "selected" : ""}`}
                        onClick={() => {
                          setSelectedTab(tab);

                          setTimeout(() => {
                            window.scrollTo({
                              top: 0,
                              behavior: "smooth",
                            });
                          }, 50);
                        }}
                      >
                        {tab}
                      </div>
                    ))}
                  </div>

                  <div ref={productTabsRef} className="product-tabs">
                    {various.productTabs.map(tab => (
                      <div
                        key={tab}
                        className={`product-tab ${selectedTab === tab ? "selected" : ""}`}
                        onClick={() => {
                          setSelectedTab(tab);

                          setTimeout(() => {
                            window.scrollTo({
                              top: 0,
                              behavior: "smooth",
                            });
                          }, 50);
                        }}
                      >
                        {tab}
                      </div>
                    ))}
                  </div>
                </div>

                {selectedTab === "Details" ? (
                  renderDetails()
                ) : selectedTab === "Videos" ? (
                  renderVideoSlides(selectedIndex)
                ) : selectedTab === "Overview" ? (
                  <div className="overview-scroll-container-mobile">{renderOverviewContent()}</div>
                ) : (
                  <div className="card-slider tv-effect">
                    <Swiper
                      key={`${selectedProduct}-${selectedTab}`}
                      effect={"coverflow"}
                      grabCursor={true}
                      modules={[Scrollbar]}
                      className="mySwiper"
                      slidesPerView={1}
                      centeredSlides={true}
                      initialSlide={slidePositions[selectedProduct][selectedTab]}
                      onSlideChange={handleSlideChange}
                      preventClicks={false}
                      preventClicksPropagation={false}
                      scrollbar={{ draggable: true }}
                    >
                      {renderSlides()}
                    </Swiper>
                  </div>
                )}
              </>
            ) : (
              <div className="select-product-message">
                <p>{various.selectProduct}</p>
              </div>
            )}
          </>
        ) : (
          <About_Mobile />
        )}
      </div>
      <div className="footer-container-ref" ref={footerRef}>
        <Footer />
      </div>
    </div>
  );
};

export default Home_Mobile;
